import type { NotificationDto } from '@/api/platform';
import { storage } from '@/lib/storage';

/** At most this many toasts per refresh (and on screen — the Toaster shows 3). */
export const MAX_TOASTS = 3;
export const TOAST_MS = 6000;

/**
 * Which notifications are new since the last look. The first call (watermark null) only records the newest id, so notifications
 * that existed before the page loaded never pop up. Ids are increasing, so "id > watermark" = created after the last look.
 * Returns oldest-first, read ones excluded (read in another tab), capped at the newest {@link MAX_TOASTS}.
 */
export function takeNew(items: readonly NotificationDto[], watermark: number | null): { fresh: NotificationDto[]; watermark: number } {
  const max = items.reduce((m, n) => Math.max(m, n.id), watermark ?? 0);
  if (watermark === null) return { fresh: [], watermark: max };
  const fresh = items
    .filter((n) => n.id > watermark && !n.readAtUtc)
    .sort((a, b) => a.id - b.id)
    .slice(-MAX_TOASTS);
  return { fresh, watermark: max };
}

// ---------- opt-in desktop notifications (per user, this browser) ----------

const desktopKey = (userId: number) => `notify.desktop.${userId}`;

export function desktopSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function desktopEnabled(userId: number | undefined): boolean {
  return !!userId && desktopSupported() && Notification.permission === 'granted' && storage.get(desktopKey(userId)) === '1';
}

export function setDesktopPref(userId: number, on: boolean) {
  if (on) storage.set(desktopKey(userId), '1');
  else storage.remove(desktopKey(userId));
}

/** Must be called from a click handler (browsers only show the permission prompt for a user gesture). */
export async function enableDesktop(userId: number): Promise<'granted' | 'denied' | 'unsupported'> {
  if (!desktopSupported()) return 'unsupported';
  const p = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
  if (p !== 'granted') return 'denied';
  setDesktopPref(userId, true);
  return 'granted';
}

/** Shows an OS notification; clicking it focuses the tab and runs `onClick`. Errors (some mobile browsers) are ignored. */
export function showDesktop(n: NotificationDto, onClick: () => void) {
  try {
    const d = new Notification(n.title, { body: n.body ?? undefined, tag: `crm-notification-${n.id}`, icon: '/favicon.svg' });
    d.onclick = () => {
      window.focus();
      onClick();
      d.close();
    };
  } catch {
    /* not allowed here */
  }
}
